import type {
  ReactNode,
  TableHTMLAttributes,
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

/**
 * Tabela que vira lista de cartões no celular.
 *
 * A medição a 360px: 7 das 8 tabelas com dados pediam mais largura do que a
 * tela tem — a lista de tenants pedia 569px para 294px úteis, com 48% do
 * conteúdo fora do aparelho e cada linha esticada a 3,7x a altura necessária.
 * Rolagem horizontal não resolvia porque nada anuncia que existe conteúdo à
 * direita, e porque a coluna do nome sozinha tem piso de 340px: um slug não
 * tem onde quebrar, então nenhum arranjo de colunas cabe.
 *
 * A saída é a mesma que o painel já usa em `InvoiceList`, `MemberList` e
 * `AuditLog`: abaixo de 768px cada linha vira um cartão e cada célula mostra o
 * próprio rótulo, via `data-label` + `::before`.
 *
 * Por que componentes em vez de atributos soltos: o padrão exige `data-label`
 * em toda célula e papéis ARIA em todo elemento — trocar `display` apaga a
 * semântica de tabela, e os três componentes do painel deixaram isso passar.
 * Seriam ~240 atributos espalhados por 12 arquivos, fáceis de esquecer num.
 * Aqui o rótulo é obrigatório pelo tipo: célula sem `label`, sem `heading` e
 * sem `action` não compila.
 */

export function CardTable({
  children,
  className,
  ...rest
}: TableHTMLAttributes<HTMLTableElement> & { children: ReactNode }) {
  return (
    <table role="table" className={['table-cards', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </table>
  );
}

export function THead({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead role="rowgroup" {...rest}>
      {children}
    </thead>
  );
}

export function TBody({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody role="rowgroup" {...rest}>
      {children}
    </tbody>
  );
}

export function Tr({ children, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr role="row" {...rest}>
      {children}
    </tr>
  );
}

export function Th({ children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th role="columnheader" {...rest}>
      {children}
    </th>
  );
}

type TdBase = Omit<TdHTMLAttributes<HTMLTableCellElement>, 'role'>;

/**
 * `heading` é o identificador do cartão — nome do restaurante, código do
 * pedido, data do lançamento. Ele leva a linha inteira e dispensa rótulo: no
 * par rótulo-à-esquerda/valor-à-direita um nome longo é espremido até virar
 * escada, que é o defeito que esta mudança existe para acabar.
 *
 * `action` é a célula de botões: sem rótulo, alinhada à direita, com o alvo de
 * toque que a coluna de 24px não tinha.
 */
type TdProps =
  | (TdBase & { label: string; heading?: never; action?: never })
  | (TdBase & { heading: true; label?: never; action?: never })
  | (TdBase & { action: true; label?: never; heading?: never })
  // Célula que atravessa a tabela — "Carregando…", "Nenhum resultado". Não
  // pertence a coluna nenhuma, então não tem rótulo a mostrar.
  | (TdBase & { colSpan: number; label?: never; heading?: never; action?: never });

export function Td({ label, heading, action, className, children, ...rest }: TdProps) {
  const classes = [
    heading ? 'cell-heading' : null,
    action ? 'cell-action' : null,
    className,
  ].filter(Boolean);

  return (
    <td
      role="cell"
      data-label={label}
      className={classes.length ? classes.join(' ') : undefined}
      {...rest}
    >
      {children}
    </td>
  );
}
